import { Dayjs } from "dayjs";
import { EAgencies, INameValue } from "src/shared/types/common.types";
import { ECurrency } from "src/shared/types/currency.types";
import {
  EContractState,
  EOriginalAvailability,
  ETaxSystem,
} from "src/shared/types/partner.types";
import {
  IContractPerson,
  IPartnerContractCustomer,
  IPartnerContractExecutor,
} from "src/shared/types/person.types";
import { TEntityWithId } from "src/shared/types/utility";

export interface IPartnerComplexContractItem {
  id: number;
  partner_id: number;
  contract_number: string;
  contract_email: string | null;
  contract_state: INameValue<EContractState>;
  virtual_contract_state?: INameValue<EContractState>;
  contract_date: string;
  site: string;
  currency: INameValue<ECurrency>;
  tax_value: number;
  partner_bonus_min_sum: number;
  tax_system: INameValue<ETaxSystem>;
  has_tax_certificate: boolean;
  tax_certificate_enabled?: boolean;
  tax_certificate_path?: File | null | string;
  tax_certificate_attach_date?: string | null;
  original_availability: INameValue<EOriginalAvailability>;
  contract_manager: IContractPerson | null;
  context_teamlead: IContractPerson | null;
  account_manager: IContractPerson | null;
  creator?: IContractPerson | null;
  with_edits: boolean;
  edit_notes: string;
  contract_original_file_enabled?: boolean;
  contract_original_attach_date?: string | null;
  contract_original_file_path?: File | null | string;
  note?: string;
  customer_info: IPartnerContractCustomer;
  executor_info: IPartnerContractExecutor;
  agency: INameValue<EAgencies>;
  is_EDMS_enabled: boolean;
  is_nda_signed: boolean;
  agreements: Array<IAgreementItem>;
}

export interface IPartnerComplexContractFormValues
  extends Omit<
    IPartnerComplexContractItem,
    | "id"
    | "contract_date"
    | "contract_state"
    | "currency"
    | "tax_system"
    | "original_availability"
    | "contract_manager"
    | "context_teamlead"
    | "account_manager"
    | "customer_info"
    | "executor_info"
    | "agency"
    | "is_EDMS_enabled"
    | "is_nda_signed"
    | "context_teamlead"
    | "partner_id"
    | "contract_creator"
    | "agreements"
  > {
  id?: number;
  partner_id: number | "";
  contract_date: Dayjs;
  contract_state: EContractState;
  currency: ECurrency;
  tax_system: ETaxSystem;
  original_availability: EOriginalAvailability;
  contract_manager: number | "";
  context_teamlead: number | "";
  account_manager: number | "";
  customer_info: Omit<IPartnerContractCustomer<string>, "id">;
  executor_info: Omit<IPartnerContractExecutor<string>, "id">;
  history_log_note?: {
    comment: string;
    history_id: number;
    guide_id?: number;
  };
}

export interface IPostPartnerComplexContractDTO
  extends Omit<
    IPartnerComplexContractFormValues,
    "contract_date" | "customer_info" | "executor_info" | "id"
  > {
  contract_date: string;
  customer_info: Omit<IPartnerContractCustomer, "id">;
  executor_info: Omit<IPartnerContractExecutor, "id">;
}

export type TPutPartnerComplexContractDTO =
  TEntityWithId<IPostPartnerComplexContractDTO>;

export interface IPartnerVirtualContractItem {
  id: number;
  contract_number: string;
  partner_id: number;
  partner_contract_id: number | null;
  state: INameValue<EContractState>;
  service_name: INameValue;
  service_currency: INameValue<ECurrency>;
  site: string;
  is_bonus_enabled: boolean;
  contract_date: string;
  note: string;
}

export interface IPartnerVirtualContractFormValues
  extends Omit<
    IPartnerVirtualContractItem,
    | "id"
    | "contract_date"
    | "service_currency"
    | "state"
    | "service_name"
    | "partner_contract_id"
  > {
  id?: number;
  partner_contract_id: number | "";
  service_currency: ECurrency | "";
  state: EContractState;
  service_name: string;
  contract_date: Dayjs;
}

export interface IPostPartnerVirtualContractDTO
  extends Omit<
    IPartnerVirtualContractFormValues,
    "contract_date" | "partner_contract_id"
  > {
  contract_date: string;
  partner_id: number;
}

export type TPutPartnerVirtualContractDTO =
  TEntityWithId<IPostPartnerVirtualContractDTO>;

export interface IVirtualContractBonusRangeItem {
  id: number;
  virtual_contract_id: number;
  spends_from: number;
  spends_percentage: number;
  last_el: 0 | 1;
}

export interface IVirtualContractBonusRanges {
  contract: IPartnerComplexContractItem;
  partner_bonuses_percents_grid: Array<IVirtualContractBonusRangeItem>;
  default_bonuses_percents_grid: Array<IVirtualContractBonusRangeItem>;
}

export interface IAgreementItem {
  id: number;
  contract_id: number;
  agreement_date: string;
  is_active: boolean;
  is_original_presented: boolean;
  is_agreement_with_edits: boolean;
  file_path: string | null;
  note: string | null;
}
export interface IAgreementMainFormData
  extends Omit<
    IAgreementItem,
    "id" | "agreement_date" | "contract_id" | "file_path"
  > {
  agreement_date: Dayjs;
  is_active: boolean;
  is_original_presented: boolean;
  is_agreement_with_edits: boolean;
  note: string | null;
}

export interface IAgreementFormValues {
  data: IAgreementMainFormData;
  file: File | null;
}

export interface IPostAgreementDTO
  extends Omit<IAgreementMainFormData, "file" | "agreement_date"> {
  agreement_date: string;
  partner_id: number;
  contract_id: number;
}
export interface IPutAgreementDTO extends IPostAgreementDTO {
  agreement_id: number;
}

// use for table rendering
export enum EContractType {
  virtual = "virtual",
  complex = "complex",
  agreement = "agreement",
}
export interface IContractAdditionalFields {
  type?: EContractType;
  contract_id?: number;
}

export interface IAgreementApiPathVariables {
  contract_id: string;
  partner_id: string;
  agreement_id?: string;
}
